// Workflow Doctor Chat — client transport.
//
// Wraps the agentic loop:
//   1. POST /workflow-doctor-chat/chat with the live workflow + user message.
//   2. If the server responds with `kind === 'pending_client_tool'`, run the
//      tool locally (via doctorChatTools.runClientTool) against the LIVE
//      canvas state and POST back to /chat/resume.
//   3. Repeat until `kind === 'final'`.
//
// Source of truth: the caller passes a getCtx() function that returns the
// current { workflowState, executionLog }. We re-query it on every resume
// so live edits are picked up between rounds.

var apiClient = require('./apiClient');
var doctorChatTools = require('./doctorChatTools');

var ENDPOINT_CHAT = '/workflow-doctor-chat/chat';
var ENDPOINT_RESUME = '/workflow-doctor-chat/chat/resume';
var ENDPOINT_CHAT_STREAM = '/workflow-doctor-chat/chat/stream';
var ENDPOINT_RESUME_STREAM = '/workflow-doctor-chat/chat/resume/stream';

var MAX_CLIENT_TOOL_ROUNDS = 12;

// ---------------------------------------------------------------------------
// Public: sendMessage — runs the full request → resume loop until final.
// ---------------------------------------------------------------------------
//
// args:
//   getCtx()  → { workflowState, executionLog }   (always returns CURRENT live state)
//   messages  → conversation history (array of { role, content, images? })
//   images    → fresh image attachments for THIS user turn (optional)
//   onClientTool({name, args, result}) → optional callback for UI tracing
//
// returns: { reply, fixPlan, toolCallsUsed, driver }

async function sendMessage(args) {
  var getCtx = args.getCtx;
  var messages = args.messages || [];
  var images = args.images || [];
  var onClientTool = typeof args.onClientTool === 'function' ? args.onClientTool : null;

  if (typeof getCtx !== 'function') {
    throw new Error('doctorChat.sendMessage: getCtx function required');
  }

  var ctx = getCtx();
  var initialResponse = await apiClient.request(ENDPOINT_CHAT, {
    method: 'POST',
    body: {
      workflowState: ctx.workflowState,
      executionLog: ctx.executionLog || null,
      messages: messages,
      images: images
    }
  });

  var current = (initialResponse && initialResponse.data) || initialResponse;
  return loopUntilFinal(current, getCtx, onClientTool);
}

async function loopUntilFinal(current, getCtx, onClientTool) {
  var rounds = 0;
  while (current && current.kind === 'pending_client_tool') {
    if (rounds++ > MAX_CLIENT_TOOL_ROUNDS) {
      throw new Error('doctorChat: exceeded client tool round limit');
    }
    var pending = current.pendingClientTool || {};
    var ctx = getCtx();
    var result = await doctorChatTools.runClientTool(pending.name, pending.args || {}, ctx);

    if (onClientTool) {
      try { onClientTool({ name: pending.name, args: pending.args, result: result }); }
      catch (e) { /* tracing failures must not break the loop */ }
    }

    var resumeResponse = await apiClient.request(ENDPOINT_RESUME, {
      method: 'POST',
      body: {
        session: current.session,
        toolResult: result,
        workflowState: ctx.workflowState,
        executionLog: ctx.executionLog || null
      }
    });
    current = (resumeResponse && resumeResponse.data) || resumeResponse;
  }

  if (!current || current.kind !== 'final') {
    throw new Error('doctorChat: unexpected response shape');
  }
  return {
    reply: current.reply || '',
    fixPlan: current.fixPlan || null,
    toolCallsUsed: current.toolCallsUsed || 0,
    driver: current.driver || ''
  };
}

// ---------------------------------------------------------------------------
// Public: sendMessageStream — same request → resume loop as sendMessage, but
// each leg is an SSE stream so the caller sees the doctor's thinking live. The
// heartbeat from the server keeps apiClient's stall watchdog fed during long
// server-side gaps (tool runs, classifier), and token deltas reset it once the
// model starts writing.
// ---------------------------------------------------------------------------
//
// extra args (on top of sendMessage's):
//   onChunk({ text, fullText })  → a text delta for the CURRENT model round
//   onRound()                    → a new model round started; clear live text
//
// returns: Promise<{ reply, fixPlan, toolCallsUsed, driver }>

function sendMessageStream(args) {
  var getCtx = args.getCtx;
  var messages = args.messages || [];
  var images = args.images || [];
  var onClientTool = typeof args.onClientTool === 'function' ? args.onClientTool : null;
  var onChunk = typeof args.onChunk === 'function' ? args.onChunk : null;
  var onRound = typeof args.onRound === 'function' ? args.onRound : null;

  if (typeof getCtx !== 'function') {
    return Promise.reject(new Error('doctorChat.sendMessageStream: getCtx function required'));
  }

  return new Promise(function(resolve, reject) {
    var rounds = 0;

    // Open one SSE leg. Resolves the whole turn (via handleResult) or rejects.
    // `settled` guards against late onClose/onError firing after a terminal
    // event already advanced the loop.
    function streamLeg(path, body) {
      var settled = false;
      apiClient.requestStream(path, { method: 'POST', body: body }, {
        onEvent: function(ev) {
          if (!ev || settled) return;
          if (ev.type === 'round') { if (onRound) onRound(); }
          else if (ev.type === 'chunk') { if (onChunk) onChunk({ text: ev.text, fullText: ev.fullText }); }
          else if (ev.type === 'result') { settled = true; handleResult(ev.result); }
          else if (ev.type === 'error') { settled = true; reject(new Error(ev.message || 'Chat failed')); }
        },
        onError: function(err) { if (!settled) { settled = true; reject(err); } },
        onClose: function() { if (!settled) { settled = true; reject(new Error('doctorChat: stream closed before a result')); } }
      });
    }

    function handleResult(current) {
      if (current && current.kind === 'pending_client_tool') {
        if (rounds++ > MAX_CLIENT_TOOL_ROUNDS) {
          reject(new Error('doctorChat: exceeded client tool round limit'));
          return;
        }
        var pending = current.pendingClientTool || {};
        var ctx = getCtx();
        doctorChatTools.runClientTool(pending.name, pending.args || {}, ctx).then(function(result) {
          if (onClientTool) {
            try { onClientTool({ name: pending.name, args: pending.args, result: result }); }
            catch (e) { /* tracing must not break the loop */ }
          }
          streamLeg(ENDPOINT_RESUME_STREAM, {
            session: current.session,
            toolResult: result,
            workflowState: ctx.workflowState,
            executionLog: ctx.executionLog || null
          });
        }).catch(reject);
        return;
      }
      if (current && current.kind === 'final') {
        resolve({
          reply: current.reply || '',
          fixPlan: current.fixPlan || null,
          toolCallsUsed: current.toolCallsUsed || 0,
          driver: current.driver || ''
        });
        return;
      }
      reject(new Error('doctorChat: unexpected response shape'));
    }

    var ctx0 = getCtx();
    streamLeg(ENDPOINT_CHAT_STREAM, {
      workflowState: ctx0.workflowState,
      executionLog: ctx0.executionLog || null,
      messages: messages,
      images: images
    });
  });
}

module.exports = {
  sendMessage: sendMessage,
  sendMessageStream: sendMessageStream
};
